import React, { useEffect } from "react";
import { isBefore, isEqual, add, format } from "date-fns";
import { inject, observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Tooltip, Avatar, Text } from "src/components";
import { theme } from "src/config";
import i18next from "src/i18n";
import { Deliverable, Question, User } from "src/models";
import { DeliverableStore, SpaceStore } from "src/stores";
import { getDateDiff, rangeOfDateInterval, startOf } from "src/util/util";
import styled from "styled-components";
import { StickyTableCell } from "../components/stickyTableCell";
import { TableDataCounter } from "../components/tableDataCounter";

export interface IDelivUserRowProps {
  start:       Date,
  end:         Date,
  view:        "days" | "weeks" | "months",
  user:        User,
  delivs:      Deliverable[], // deliverables to @user, sorted by date
  questions:   Question[], // questions on the board to @user, sorted by date
  delivStore?: DeliverableStore;
  spaceStore?: SpaceStore;
}
export const DelivUserRow = inject("delivStore", "spaceStore")(observer((props: IDelivUserRowProps) => {
  const { start, end, view, user, delivs, questions, delivStore, spaceStore } = props;
  const { t, i18n } = useTranslation();

  const cells = [];
  const _delivs:    Deliverable[] = delivs?.slice()    || [];
  const _questions: Question[]    = questions?.slice() || [];
  for (let curDate = startOf(start, view); isBefore(curDate, end) || isEqual(curDate, end); curDate = add(curDate, { [view]: 1 })) {
    const d: Deliverable[] = [];
    const q: Question[]    = [];

    while (_delivs.length && getDateDiff(_delivs[0].date, curDate, view) < 1) d.push(_delivs.shift());
    while (_questions.length && getDateDiff(_questions[0].date, curDate, view) < 1) q.push(_questions.shift());

    const numDelivered   = d.filter((deliv) => deliv.delivered).length;
    const numUndelivered = d.length - numDelivered;
    const numQuestions   = q.length;

    const isPast = isBefore(curDate, startOf(new Date(), view));

    cells.push(
      <DelivCell key={curDate.toString()} onClick={() => {
        delivStore.setFilterValue("date", rangeOfDateInterval(curDate, view), true);
        delivStore.setFilterValue("to", [{key: user.id, label: user.getFullName, value: user}], false);
        delivStore.updateQuery();
        delivStore.advSearch = true;
      }}>
          {Boolean(numQuestions) && 
            <Tooltip content={`${t("view_all_questions_assigned") } ${ user.getFullName } ${t("on")} ${format(curDate, "yyyy-MM-dd")}`}>
              <TableDataCounter className={d.length ? "question-counter" : undefined} variant={isPast ? "meeting" : "milestone"}>
                <span>{numQuestions}</span>
              </TableDataCounter>
            </Tooltip>
          }
          {d.length > 0 && (
            <OnBoardDeliv>
              <Tooltip
                content={
                  t("view_all_delivs", { user: user.getFullName }) +
                  " " +
                  t("on") +
                  " " +
                  format(curDate, "yyyy-MM-dd")
                }
              >
              {d.length} st
              <CounterContainer>
                {numUndelivered > 0 && (
                  <StatusCounter variant="undelivered">{numUndelivered}</StatusCounter>
                )}

                {numDelivered > 0 && (
                  <StatusCounter variant="delivered">{numDelivered}</StatusCounter>
                )}
              </CounterContainer>
              </Tooltip>
            </OnBoardDeliv>
          )}
      </DelivCell>
    );
  }

  return (
    <>
      <StyledStickyTableCell>
        <Tooltip
          content={i18next.t("view_all_deliv_user", {
            user: user.getFullName
          })}
        >
          <InnerCell onClick={() => {
            delivStore.setFilterValue("to", [{key: user.id, label: user.getFullName, value: user}], true);
            delivStore.updateQuery();
            delivStore.advSearch = true;
            }}>
              <Avatar
                backgroundColor = {theme.color.grayDark}
                initials        = {user.getInitials}
                size            = "medium"
              />
            <Text size="small" weight="normal" color="grayDark">
              {spaceStore?.getCurRoom?.member_listing === "title" ? user.getTitle : user.getFullName}
            </Text>
          </InnerCell>
        </Tooltip>
      </StyledStickyTableCell>
      {cells}
    </>
  );
}));

const StyledStickyTableCell = styled(StickyTableCell)`
  min-width:  12rem;
  height:     10rem;
  z-index:    5;

&:last-of-type {
      border-right: 0;
    }

  :first-of-type {
      border-top: none;
    }

  :first-of-type {
      border-left: none;
    }

  :hover {
    background-color: ${theme.color.white};
  }
`

const DelivCell = styled.td`
  .question-counter {
    top:  12px;
    left: -30px;
  }
`;

const OnBoardDeliv = styled.div`
  display:        flex;
  flex-direction: column;
  margin:         auto;
  min-width:      8rem;
  min-height:     8rem;
  max-width:      8rem;
  padding:        1rem;
  height:         fit-content;
  border-radius:  0.3rem;
  font-weight:    ${theme.font.weight.normal};
  font-size:      ${theme.font.size.small};
  background:     ${theme.color.cards.deliverable};
  box-shadow:     ${theme.boxShadow.small};
  cursor:         pointer;
`;

const InnerCell = styled.div`
  display:         flex;
  flex-direction:  column;
  justify-content: center;
  align-items:     center;
  height:          8rem; /* change the height of the table cell of tbody */
`;

const StatusCounter = styled.div<{variant: "delivered" | "undelivered"}>`
  border-radius: 50%;
  padding:       0.2rem;
  min-width:     2rem;
  min-height:    2rem;
  margin:        0.3rem;
  background:    ${(p) => p.variant === "delivered" ? theme.color.status.delivered : theme.color.status.active };   
  font-size:     ${theme.font.size.xsmall};
  color:         white;
  text-align:    center;
`;

const CounterContainer = styled.div`
  display:         flex;
  flex-wrap:       wrap;
  justify-content: center;
`;
